/**
 * Hover and resting-caret cards for the ProseMirror editor (#250). Sources
 * (Harper fixes, entity peek) say what sits at a position; this plugin owns
 * the timing and the single shared popover:
 *
 * - Pointer rests on a target for its `hoverDelayMs` → card opens. It stays
 *   open while the pointer is on the target or the card.
 * - Caret rests inside a target with a `caretDelayMs` → same card; it closes
 *   when the caret leaves the range.
 * - Any document change or Escape closes it.
 */

import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { createTimeoutHandle } from "../../lib/timeoutHandle";
import type { ProsePopover } from "./popover";

export type HoverTarget = {
  from: number;
  to: number;
  above: boolean;
  /** Popover class for this kind of card. */
  className: string;
  render: () => HTMLElement;
};

export type HoverSource = {
  hoverDelayMs: number;
  /** Open for a resting caret too (entity peek); omit for hover only. */
  caretDelayMs?: number;
  at(view: EditorView, pos: number, side: -1 | 1): HoverTarget | null;
};

/** Pointer gap allowed between the target and the card before closing. */
const LEAVE_GRACE_MS = 150;

const hoverKey = new PluginKey("zwHoverCards");

function sameTarget(a: HoverTarget | null, b: HoverTarget | null): boolean {
  return (
    a !== null &&
    b !== null &&
    a.from === b.from &&
    a.to === b.to &&
    a.className === b.className
  );
}

function targetAt(
  view: EditorView,
  sources: readonly HoverSource[],
  pos: number,
  side: -1 | 1,
  caret: boolean,
): { source: HoverSource; target: HoverTarget } | null {
  for (const source of sources) {
    if (caret && source.caretDelayMs === undefined) continue;
    const target = source.at(view, pos, side);
    if (target !== null) return { source, target };
  }
  return null;
}

/** Document position under the pointer and which side of it the pointer is on. */
function pointerPos(
  view: EditorView,
  event: MouseEvent,
): { pos: number; side: -1 | 1 } | null {
  const found = view.posAtCoords({ left: event.clientX, top: event.clientY });
  if (found === null || found.inside < 0) return null;
  const coords = view.coordsAtPos(found.pos);
  return { pos: found.pos, side: event.clientX < coords.left ? -1 : 1 };
}

export function hoverCardsPlugin(
  card: ProsePopover,
  sources: readonly HoverSource[],
): Plugin {
  const openTimer = createTimeoutHandle();
  const closeTimer = createTimeoutHandle();
  const caretTimer = createTimeoutHandle();
  let pending: HoverTarget | null = null;
  let open: HoverTarget | null = null;
  let openedByCaret = false;

  function close(): void {
    openTimer.clear();
    closeTimer.clear();
    caretTimer.clear();
    pending = null;
    open = null;
    openedByCaret = false;
    card.hide();
  }

  function show(target: HoverTarget, byCaret: boolean): void {
    closeTimer.clear();
    open = target;
    openedByCaret = byCaret;
    card.show({ from: target.from, to: target.to }, target.render(), {
      above: target.above,
      className: target.className,
    });
  }

  function scheduleClose(): void {
    if (open === null || openedByCaret) return;
    closeTimer.schedule(LEAVE_GRACE_MS, close);
  }

  function onPointer(view: EditorView, event: MouseEvent): void {
    const at = pointerPos(view, event);
    const hit =
      at === null ? null : targetAt(view, sources, at.pos, at.side, false);
    if (hit === null) {
      openTimer.clear();
      pending = null;
      scheduleClose();
      return;
    }
    if (sameTarget(hit.target, open)) {
      closeTimer.clear();
      return;
    }
    if (sameTarget(hit.target, pending)) return;
    pending = hit.target;
    openTimer.schedule(hit.source.hoverDelayMs, () => {
      if (pending !== null) show(pending, false);
      pending = null;
    });
  }

  function scheduleCaret(view: EditorView): void {
    caretTimer.clear();
    const { selection } = view.state;
    if (openedByCaret && open !== null) {
      const head = selection.head;
      if (selection.empty === false || head < open.from || head > open.to) {
        close();
      }
    }
    if (view.hasFocus() === false || selection.empty === false) return;
    const head = selection.head;
    const hit =
      targetAt(view, sources, head, 1, true) ??
      targetAt(view, sources, head, -1, true);
    if (hit === null || sameTarget(hit.target, open)) return;
    const target = hit.target;
    caretTimer.schedule(hit.source.caretDelayMs ?? 0, () => {
      if (view.hasFocus() === false || view.state.selection.empty === false)
        return;
      const now = view.state.selection.head;
      if (now < target.from || now > target.to) return;
      show(target, true);
    });
  }

  return new Plugin({
    key: hoverKey,
    props: {
      handleKeyDown(_view, event) {
        if (event.key !== "Escape" || open === null) return false;
        close();
        return true;
      },
      handleDOMEvents: {
        mousemove(view, event) {
          onPointer(view, event);
          return false;
        },
        mouseleave(_view, event) {
          openTimer.clear();
          pending = null;
          if (card.contains(event.relatedTarget as Node | null)) return false;
          scheduleClose();
          return false;
        },
        focus(view) {
          scheduleCaret(view);
          return false;
        },
        blur() {
          caretTimer.clear();
          return false;
        },
      },
    },
    view(view) {
      const onEnter = (): void => closeTimer.clear();
      const onLeave = (event: MouseEvent): void => {
        if (view.dom.contains(event.relatedTarget as Node | null)) return;
        scheduleClose();
      };
      card.dom.addEventListener("mouseenter", onEnter);
      card.dom.addEventListener("mouseleave", onLeave);
      scheduleCaret(view);
      return {
        update(current, prev) {
          if (current.state.doc !== prev.doc) {
            close();
          }
          if (
            current.state.doc !== prev.doc ||
            current.state.selection !== prev.selection
          ) {
            scheduleCaret(current);
          }
        },
        destroy() {
          card.dom.removeEventListener("mouseenter", onEnter);
          card.dom.removeEventListener("mouseleave", onLeave);
          close();
        },
      };
    },
  });
}
